import { ZodError } from 'zod'
import { HOST } from '@/config/consts'
import {
  PointsArray,
  PointsArraySchema,
  ConversionWithPayoutTerms,
  ExternalPlatform,
  Platform,
  EntryType,
  PointsObject,
} from './types'

import { zeroAddress } from 'viem'
import { CheckedAddress } from '../types/CheckedAddress'
import { Address } from 'viem'
import { getAddressBySymbol, TokenSymbolEnum } from '@/utils/tokens'

export function formatName(name: string) {
  // Misspelled symbol and name doesn't follow the same format
  if (name === 'Hold debtUSDLX - hyper-evm') {
    return 'Pooled Borrow USDXL'
  }

  // Name was set to the description
  if (name === 'Borrow 0xc7e7861352df6919e7152C007832C48A777f2a4c - 0xA3538B5bCfD55d39B0fFd17D8a5fba01a7AF648E') {
    return 'HypurrFi Yield - Euler WHYPE Vault - Borrow'
  }

  return name
}

export function safeValidatePointsData(data: unknown): { success: boolean; data?: PointsArray; error?: ZodError } {
  return PointsArraySchema.safeParse(data)
}

const protocolImageBaseUrl = `${HOST}/assets/protocols`

export function getProtocolImage(protocol?: string): string {
  if (!protocol) return ''
  return `${protocolImageBaseUrl}/${protocol.toLowerCase().replace(' ', '-')}.svg`
}

export function normalizePlatform(_name: string): Platform {
  const name = _name.toLowerCase()
  switch (true) {
    case name.includes('balancer'):
      return 'balancer'
    case name.includes('hybra'):
      return 'hybra'
    case name.includes('hyperswap'):
      return 'hyperswap'
    case name.includes('kittenswap'):
      return 'kittenswap'
    case name.includes('pendle'):
      return 'pendle'
    case name.includes('valantis'):
      return 'valantis'
    default:
      return 'hypurrfi'
  }
}

export const externalLinksConfig = {
  specific: {
    'Provide Liquidity to hyWHYPE pool on Pendle':
      'https://app.pendle.finance/trade/pools/0x438a5973eed017e9b4e6a0dcfcfae67290b07ee0/zap/in?chain=hyperevm',
    'hwHLP / USDT0 Balancer': 'https://balancer.fi/pools/hyperevm/v3/0x768e4f8219095d714bc7dcfd60a5ece67d32faf9',
    'Hybra WHYPE-USDXL':
      'https://www.hybra.finance/liquidity/add?token0=0x5555555555555555555555555555555555555555&token1=0xca79db4b49f608ef54a5cb813fbed3a6387bc645&fee=2500&type=v3',
  } as Record<string, string>,
  protocols: {
    balancer: 'https://balancer.fi',
    hybra: 'https://www.hybra.finance/liquidity',
    hyperswap: 'https://app.hyperswap.exchange/#/explore/explore-pools',
    kittenswap: 'https://app.kittenswap.finance/pools',
    pendle: 'https://app.pendle.finance/trade/pools',
    valantis: 'https://app.valantis.xyz/',
  } as Record<ExternalPlatform, string>,
  default: '',
}

// Matches name of Fuul conversion to an external link and sets fallback
// external link if external protocol name is mentioned
export function getExternalLink(name: string): string {
  if (name in externalLinksConfig.specific) {
    return externalLinksConfig.specific[name] as string
  }

  const protocolKeyword = Object.keys(externalLinksConfig.protocols).find((keyword) =>
    name.toLowerCase().includes(keyword),
  )

  if (protocolKeyword) {
    return externalLinksConfig.protocols[protocolKeyword as ExternalPlatform]
  }

  return externalLinksConfig.default
}

// Some Fuul conversions have token set to different oracle (eg, USDC uses the USDT0 oracle)
// causing a different address to be used for the token volume currency address
// so we match by hyToken instead
export const hyToTokenAddress: Record<Address, Address> = {
  '0x280535137Dd84080d97d0826c577B4019d8e1BEb': CheckedAddress(getAddressBySymbol(TokenSymbolEnum.USDC)),
  '0x3b967db41B6bE6A42B3F42077Ee707aF2F45713e': CheckedAddress(getAddressBySymbol(TokenSymbolEnum.BEHYPE)),
  '0x68A0d8F2dfe817F3A6Aa85EE430D42BA256fC8f4': CheckedAddress(getAddressBySymbol(TokenSymbolEnum.PTKHYPE13NOV2025)),
  '0xB55780B08b18A57590e7B2729CaeA5a6818Ba035': CheckedAddress(getAddressBySymbol(TokenSymbolEnum.PTKHYPE19MAR2026)),
  '0x5F137306A1692207624FB0f012ac19fD42698756': CheckedAddress(getAddressBySymbol(TokenSymbolEnum.USDH)),
}

export function normalizeAddress(entry: ConversionWithPayoutTerms): Address | undefined {
  const { triggers } = entry
  const pairAddress = triggers?.find((trigger) => trigger.context.pairAddress)?.context.pairAddress

  if (pairAddress) {
    return CheckedAddress(pairAddress)
  }

  const isPooledEntry = entry.name.toLocaleLowerCase().includes('pooled') || entry.name === 'Hold debtUSDLX - hyper-evm'

  if (isPooledEntry) {
    const standardTrigger = triggers?.find((trigger) => trigger.triggerType === 'hypurr-net-pool-positions')

    if (!standardTrigger) {
      const usdxlTrigger = triggers?.find(
        (trigger) =>
          trigger.volume_currency_expression?.toLowerCase() === getAddressBySymbol(TokenSymbolEnum.USDXL).toLowerCase(),
      )

      return usdxlTrigger ? CheckedAddress(usdxlTrigger.volume_currency_expression as string) : undefined
    }

    const hyTokenAddress = standardTrigger?.context.hyTokenAddress
    const override = hyTokenAddress ? hyToTokenAddress[hyTokenAddress as Address] : undefined

    if (override) {
      return CheckedAddress(override)
    }

    const volumeCurrencyAddress = standardTrigger?.context.volumeCurrencyAddress

    // HYPE is set to zero address in the Fuul conversion
    if (volumeCurrencyAddress === zeroAddress) {
      return CheckedAddress(getAddressBySymbol(TokenSymbolEnum.WHYPE))
    }

    return volumeCurrencyAddress ? CheckedAddress(volumeCurrencyAddress) : undefined
  }

  const isStakedEntry = entry.name.toLowerCase().startsWith('stake')

  if (isStakedEntry) {
    const address = triggers?.find((trigger) => !!trigger.context.token_address)?.context.token_address

    return address ? CheckedAddress(address) : undefined
  }

  const isEulerEntry = entry.name.toLowerCase().includes('euler')

  if (isEulerEntry) {
    const trigger = triggers?.find((trigger) => trigger.triggerType === 'hypurr-euler-net-positions')

    // @NOTE: Even borrows use the deposit token address
    const address = trigger?.context.depositTokenAddress

    return address ? CheckedAddress(address) : undefined
  }
}

export function getEntryType(entry: ConversionWithPayoutTerms): EntryType | undefined {
  const name = entry.name.toLowerCase()

  switch (true) {
    case name.startsWith('pooled') || name.startsWith('net pooled'):
      return {
        type: 'pooled',
        action_type: name.includes('deposit') ? 'deposit' : 'borrow',
      }
    case name.startsWith('isolated'):
      return {
        type: 'isolated',
        action_type: name.includes('deposit') ? 'deposit' : 'borrow',
      }
    case name.startsWith('stake'):
      return {
        type: 'vault',
        action_type: 'deposit',
      }
    case name.toLowerCase().includes('euler'):
      return {
        type: 'euler',
        action_type: name.includes('deposit') ? 'deposit' : 'borrow',
      }
    case name.startsWith('hold debtusdlx'):
      return {
        type: 'pooled',
        action_type: 'borrow',
      }
    default:
      return {
        type: 'unknown',
        action_type: 'unknown',
      }
  }
}

export const getPointsEntryByAddress = (
  data: PointsArray,
  params: { type: string; action: string; address: Address },
): PointsObject | undefined => {
  const { type, action, address } = params

  return data.find((point) => {
    return (
      point.type.toLowerCase() === type.toLowerCase() &&
      point.action_type.toLowerCase() === action.toLowerCase() &&
      !!point.address &&
      point.address.toLowerCase() === address.toLowerCase()
    )
  })
}
